import React, { useEffect, useMemo, useRef, useState } from "react";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { useAuth } from "../../contexts/AuthContext";
import { listMyBookings } from "../../lib/mudoNetwork/bookingsApi";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function titleForBooking(b) {
  if (!b) return "Untitled";
  if (b.type === "workshop") return b.workshop?.title || "Workshop";
  if (b.type === "space") {
    const spaceName = b.space_slot?.space?.name;
    if (spaceName) return `Studio booking: ${spaceName}`;
    return "Studio booking";
  }
  return b.workshop?.title || "Booking";
}

function notesForBooking(b) {
  const meta = b?.metadata;
  return (
    b?.attendee_message ||
    meta?.attendee_message ||
    meta?.message ||
    meta?.note ||
    ""
  );
}

function badgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("ready") || s.includes("complete") || s.includes("completed")) {
    return "bg-emerald-500/15 text-emerald-200 border-emerald-500/30";
  }
  if (s.includes("paid") || s.includes("confirmed") || s.includes("booked")) {
    return "bg-sky-500/15 text-sky-200 border-sky-500/30";
  }
  if (s.includes("cancel")) {
    return "bg-red-500/15 text-red-200 border-red-500/30";
  }
  if (s.includes("pending")) {
    return "bg-amber-500/15 text-amber-200 border-amber-500/30";
  }
  return "bg-white/10 text-white/80 border-white/10";
}

export default function MyPiecesPage() {
  const { user, authLoading, isAuthReady } = useAuth() || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);

  const fetchInFlight = useRef(false);

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!user?.id) {
        setLoading(false);
        setBookings([]);
        return;
      }

      if (fetchInFlight.current) return;
      fetchInFlight.current = true;

      setLoading(true);
      setError(null);

      try {
        const { data, error: e } = await listMyBookings(user.id, { limit: 100 });
        if (e) throw e;
        if (!alive) return;
        setBookings(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!alive) return;
        setError(err?.message || "Failed to load pieces");
        setBookings([]);
      } finally {
        if (!alive) return;
        setLoading(false);
        fetchInFlight.current = false;
      }
    }

    // Wait for auth to settle (prevents flicker when session restores)
    if (authLoading || !isAuthReady) return;

    run();
    return () => {
      alive = false;
    };
  }, [user?.id, authLoading, isAuthReady]);

  const rows = useMemo(() => {
    return (bookings || []).map((b) => {
      const updated = b.updated_at || b.created_at;
      return {
        id: b.id,
        title: titleForBooking(b),
        studioName: b.studio?.name || "—",
        status: b.status || "—",
        updatedAt: updated,
        notes: notesForBooking(b),
      };
    });
  }, [bookings]);

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white">
      <VisitorNavBar showBackButton backTo="/discover/profile" />

      <div className="pt-24 px-4 pb-10 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">My Pieces</h1>
            <p className="text-sm text-white/70 mt-1">
              Track your workshop pieces and studio service progress.
            </p>
          </div>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-3 py-2 rounded-xl bg-white/10 border border-white/15 hover:bg-white/15 text-sm"
            title="Refresh"
          >
            Refresh
          </button>
        </div>

        {!user && !(authLoading || !isAuthReady) && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="font-semibold">Sign in to see your pieces</div>
            <div className="text-sm text-white/70 mt-1">
              Your pieces are linked to your account.
            </div>
          </div>
        )}

        {loading && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
            Loading your pieces…
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
            <div className="font-semibold text-red-200">Couldn’t load pieces</div>
            <div className="text-sm text-red-200/80 mt-1">{error}</div>
          </div>
        )}

        {!loading && !error && user && rows.length === 0 && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="font-semibold">No tracked pieces yet</div>
            <div className="text-sm text-white/70 mt-1">
              After you book a workshop or studio service, your progress will appear here.
            </div>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="mt-6 grid gap-3">
            {rows.map((r) => (
              <div
                key={r.id}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/7 transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate" title={r.title}>
                      {r.title}
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      Studio: <span className="text-white/85">{r.studioName}</span>
                    </div>
                  </div>

                  <span
                    className={
                      "shrink-0 px-2.5 py-1 rounded-full text-[11px] border " +
                      badgeClass(r.status)
                    }
                    title="Current status"
                  >
                    {String(r.status)}
                  </span>
                </div>

                <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-white/70">
                  <div>
                    Last updated: <span className="text-white/85">{formatWhen(r.updatedAt)}</span>
                  </div>
                  {r.notes ? (
                    <div className="sm:text-right">
                      Notes: <span className="text-white/85">{r.notes}</span>
                    </div>
                  ) : (
                    <div className="sm:text-right">Notes: <span className="text-white/50">—</span></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
